/**
 * GamePlanView.jsx  v3
 * ─────────────────────
 * Data Lab "Game Plan" tab — plan vs actual analysis.
 * Uses session_plans table (same as diary) with allocation workflow.
 *
 * Allocation status:
 *   logged   — result is set
 *   pending  — 0–2 days past scheduled_date, no result yet
 *   missed   — 2+ days past scheduled_date, no result
 *   upcoming — scheduled_date is in the future
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { onEnterOrSpace } from '../utils/a11y';
import supabase from '../supabaseClient';
import { fetchAllPlans } from '../utils/plans';
import { netPlayLabel, netPlayIcon } from '../utils/sportLabels';
import { toLocalISO, todayLocal } from '../utils/dateLocal';
import { opponentLabel, partnerLabel, formatBadge } from '../utils/matchFormat';
import { useSportFiltered } from '../utils/sportScope';
import FormSituationPanel from './FormSituationPanel';
import { EquipmentStatsView, fmtStringing } from './EquipmentManager';
import {
  LineChart, Line, ScatterChart, Scatter, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ── Design ────────────────────────────────────────────────────────────────────
const C = {
  bg:      '#0d1117',
  card:    '#161b22',
  border:  'rgba(255,255,255,0.18)',
  text:    '#e6edf3',
  muted:   '#a8b3bd',
  accent:  '#3dbfb0',
  logoBlue: '#38bdf8',
  purple:  '#8b5cf6',
  font:    "'Inter', system-ui, sans-serif",
};
const TRAINING_COLOR = '#7dd3fc'; // same light blue as the training dots on Home

const STATUS = {
  logged:   { label: 'Logged',   color: '#3dbfb0', bg: '#3dbfb022', icon: '✅', desc: 'Manually logged' },
  pending:  { label: 'Scheduled', color: '#f59e0b', bg: '#f59e0b22', icon: '⏳', desc: 'Within 2 days — allocate soon' },
  missed:   { label: 'Missed',    color: '#ef4444', bg: '#ef444422', icon: '❌', desc: '2+ days past — not allocated' },
  upcoming: { label: 'Scheduled', color: '#8b5cf6', bg: '#8b5cf622', icon: '🎯', desc: 'Future planned session' },
};

const SPORT_ICONS = {
  running:'🏃', cycling:'🚴', swimming:'🏊', tennis:'🎾', padel:'🏸',
  gym:'🏋️', hiking:'🥾', triathlon:'🏅', football:'⚽', other:'🎯',
  strength:'🏋️', yoga:'🧘', crossfit:'💪', trail_running:'🏔️',
  mountain_biking:'🚵', basketball:'🏀', volleyball:'🏐', golf:'⛳',
};


const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function toISO(d) { return toLocalISO(d); }
function today() { return todayLocal(); }
function fmtDate(iso) {
  if (!iso) return '';
  // scheduled_date sometimes arrives as a full timestamp (e.g. from a
  // Supabase timestamptz column) rather than a plain "YYYY-MM-DD" —
  // slice to the date part first, or appending "T12:00:00" produces an
  // invalid double-time string that renders as "NaN undefined".
  const d = new Date(iso.slice(0, 10) + 'T12:00:00');
  return `${d.getDate()} ${MONTH_SHORT[d.getMonth()]}`;
}
function daysPast(iso) {
  const d = new Date(iso + 'T00:00:00');
  const t = new Date(); t.setHours(0,0,0,0);
  return Math.floor((t - d) / 86400000);
}

function getAllocationStatus(plan) {
  const todayStr = today();
  if (plan.scheduled_date > todayStr) return 'upcoming';
  if (plan.result || plan.performance_score) return 'logged';
  const past = daysPast(plan.scheduled_date);
  if (past <= 2) return 'pending';
  return 'missed';
}

// ── Session Detail Modal ──────────────────────────────────────────────────────

function SessionDetailModal({ plan, onClose, demoMode = false, onOpenPlan }) {
  const status = getAllocationStatus(plan);
  const sc = STATUS[status];

  // Equipment snapshot — fetched on demand since this modal only opens
  // occasionally; not worth threading racket/stringing/shoes names
  // through every tab that can open it. Demo mode has no authenticated
  // session, so a direct query is RLS-blocked regardless of filter shape —
  // pull from the public demo endpoint and filter client-side instead.
  const [equipment, setEquipment] = useState(null);
  useEffect(() => {
    const ids = [plan.equipment_racket_id, plan.equipment_shoes_id].filter(Boolean);
    if (!ids.length && !plan.equipment_stringing_id) { setEquipment(null); return; }
    let cancelled = false;

    const fetchPromise = demoMode
      ? fetch(`${API}/game-plans/demo/equipment`).then(r => r.ok ? r.json() : {})
          .then(d => [{ data: d.items || [] }, { data: (d.stringings || []).find(s => s.id === plan.equipment_stringing_id) || null }])
      : Promise.all([
          ids.length ? supabase.from('equipment_items').select('*').in('id', ids) : Promise.resolve({ data: [] }),
          plan.equipment_stringing_id ? supabase.from('equipment_stringings').select('*').eq('id', plan.equipment_stringing_id).maybeSingle() : Promise.resolve({ data: null }),
        ]);

    fetchPromise.then(([{ data: items }, { data: stringing }]) => {
      if (cancelled) return;
      const racket = (items || []).find(i => i.id === plan.equipment_racket_id) || null;
      const shoes = (items || []).find(i => i.id === plan.equipment_shoes_id) || null;
      setEquipment({ racket, shoes, stringing });
    });
    return () => { cancelled = true; };
  }, [plan.equipment_racket_id, plan.equipment_shoes_id, plan.equipment_stringing_id, demoMode]);

  const INTENSITY_LABELS = {
    1: { label: 'Recovery', color: '#22c55e' },
    2: { label: 'Aerobic / Easy', color: '#84cc16' },
    3: { label: 'Moderate', color: '#f59e0b' },
    4: { label: 'Hard / Threshold', color: '#f97316' },
    5: { label: 'Max effort', color: '#ef4444' },
  };

  const row = (label, value, valueStyle = {}) => value ? (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: `1px solid ${C.border}` }}>
      <span style={{ color: C.muted, fontSize: 12 }}>{label}</span>
      <span style={{ color: C.text, fontSize: 12, fontWeight: 600, ...valueStyle }}>{value}</span>
    </div>
  ) : null;

  // Escape-to-close — same pattern as the other modal backdrops in this
  // app; the explicit close affordance inside already covers Tab+Enter.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#1a2133', border: `1px solid ${C.border}`,
        borderRadius: 16, padding: 24, width: '100%', maxWidth: 520,
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{
                background: sc.bg, color: sc.color, border: `1px solid ${sc.color}40`,
                borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700,
              }}>{sc.icon} {sc.label}</span>
            </div>
            <div style={{ color: C.text, fontWeight: 700, fontSize: 17 }}>
              {SPORT_ICONS[plan.sport] || '🎯'} {fmtDate(plan.scheduled_date)} · {plan.type || 'Training'}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: C.muted, fontSize: 22, cursor: 'pointer',
          }}>×</button>
        </div>

        {onOpenPlan && (
          <button onClick={onOpenPlan} style={{
            display: 'block', width: '100%', background: 'rgba(61,191,176,0.12)',
            border: `1px solid ${C.accent}55`, color: C.accent, borderRadius: 10,
            padding: '9px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer',
            fontFamily: C.font, marginBottom: 20,
          }}>Open in Game Plan →</button>
        )}

        {/* Plan section */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ color: C.muted, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>📋 Plan</div>
          {row('Sport', plan.sport)}
          {row('Type', plan.type)}
          {row('Goal', plan.goal)}
          {plan.game_plan && row('Game plan', plan.game_plan)}
          {row('Duration (target)', plan.estimated_duration ? `${plan.estimated_duration} min` : null)}
          {plan.intensity_level && row('Intensity', `${plan.intensity_level} — ${INTENSITY_LABELS[plan.intensity_level]?.label}`, {
            color: INTENSITY_LABELS[plan.intensity_level]?.color,
          })}
          {plan.mental_readiness > 0 && row('Mental readiness', '★'.repeat(plan.mental_readiness) + '☆'.repeat(5 - plan.mental_readiness))}
          {plan.physical_readiness > 0 && row('Physical readiness', '★'.repeat(plan.physical_readiness) + '☆'.repeat(5 - plan.physical_readiness))}
          {plan.opponent_name && row('Opponent', opponentLabel(plan))}
          {partnerLabel(plan) && row('Partner', plan.partner_name)}
        </div>

        {/* Result section */}
        {(plan.result || plan.performance_score || plan.post_notes || plan.actual_duration_minutes || plan.avg_heart_rate || plan.calories) && (
          <div style={{
            background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.border}`,
            borderRadius: 12, padding: '12px 14px', marginBottom: 20,
          }}>
            <div style={{ color: C.muted, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
              📝 Result
            </div>
            {plan.result && row('Outcome', plan.result.replace(/_/g, ' ').toUpperCase())}
            {plan.performance_score > 0 && row('Performance', '★'.repeat(plan.performance_score) + '☆'.repeat(5 - plan.performance_score))}
            {plan.game_plan_adherence > 0 && row('Plan outcome', '★'.repeat(plan.game_plan_adherence) + '☆'.repeat(5 - plan.game_plan_adherence))}
            {plan.serve_rating > 0 && row('🎾 Serve', '★'.repeat(plan.serve_rating) + '☆'.repeat(5 - plan.serve_rating))}
            {plan.return_rating > 0 && row('↩️ Return', '★'.repeat(plan.return_rating) + '☆'.repeat(5 - plan.return_rating))}
            {plan.forehand_rating > 0 && row('💪 Forehand', '★'.repeat(plan.forehand_rating) + '☆'.repeat(5 - plan.forehand_rating))}
            {plan.backhand_rating > 0 && row('🤚 Backhand', '★'.repeat(plan.backhand_rating) + '☆'.repeat(5 - plan.backhand_rating))}
            {plan.net_play_rating > 0 && row(`${netPlayIcon(plan.sport)} ${netPlayLabel(plan.sport)}`, '★'.repeat(plan.net_play_rating) + '☆'.repeat(5 - plan.net_play_rating))}
            {plan.actual_duration_minutes > 0 && row('⏱ Duration', `${plan.actual_duration_minutes} min`)}
            {plan.avg_heart_rate > 0 && row('❤️ Avg heart rate', `${plan.avg_heart_rate} bpm`)}
            {plan.calories > 0 && row('🔥 Calories', `${plan.calories} kcal`)}
            {plan.post_notes && (
              <div style={{ color: C.muted, fontSize: 11, marginTop: 8, lineHeight: 1.5, fontStyle: 'italic' }}>
                "{plan.post_notes}"
              </div>
            )}
          </div>
        )}

        {/* Equipment — snapshotted at save time, so this shows the exact
            gear this session was played with even if defaults changed since. */}
        {equipment && (equipment.racket || equipment.shoes) && (
          <div style={{
            background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.border}`,
            borderRadius: 12, padding: '12px 14px', marginBottom: 20,
          }}>
            <div style={{ color: C.muted, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
              🎾 Equipment
            </div>
            {equipment.racket && row('Racket', `${equipment.racket.brand} ${equipment.racket.model}`)}
            {equipment.stringing && row('Strings', fmtStringing(equipment.stringing))}
            {equipment.shoes && row('Shoes', `${equipment.shoes.brand} ${equipment.shoes.model}`)}
          </div>
        )}

        <button onClick={onClose} style={{
          width: '100%', padding: '10px', borderRadius: 10,
          background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.border}`,
          color: C.muted, cursor: 'pointer', fontSize: 13, fontFamily: C.font,
        }}>Close</button>
      </div>
    </div>
  );
}



// ── Upcoming Game Plans ─────────────────────────────────────────────────────────





// ── Match Outcomes View ───────────────────────────────────────────────────────

/**
 * Formats match_score JSONB into a compact human-readable string for any sport type.
 */


// ── Main Component ────────────────────────────────────────────────────────────

// ── Game Plans Dashboard ────────────────────────────────────────────────────────

const SPORT_EMOJIS = {
  tennis:'🎾',padel:'🏸',squash:'🟡',badminton:'🏸',football:'⚽',basketball:'🏀',
  volleyball:'🏐',hockey:'🏒',running:'🏃',cycling:'🚴',swimming:'🏊',
  trail_running:'🏔️',triathlon:'🏅',strength:'🏋️',crossfit:'💪',yoga:'🧘',
  climbing:'🧗',golf:'⛳',boxing:'🥊',mma:'🥋',other:'📊',
};

const TRAINING_TYPES = ['training', 'brick', 'strength', 'recovery', 'other'];
function isCompleted(p) { return !!(p.result || p.performance_score); }
function isPending(p) {
  const t = today();
  return !isCompleted(p) && (p.scheduled_date || '').slice(0,10) <= t;
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

function SessionsDashboard({ plans, userId, activeSport, onOpenDetail, demoMode = false }) {
  const isMobile = useIsMobile();

  const todayStr = today();
  const allPast = plans.filter(p => (p.scheduled_date || '').slice(0,10) <= todayStr);
  const completed  = allPast.filter(isCompleted);
  const pending    = allPast.filter(isPending);
  const perfScores = completed.filter(p => p.performance_score).map(p => p.performance_score);
  const avgPerf    = perfScores.length ? (perfScores.reduce((a,b)=>a+b,0)/perfScores.length).toFixed(1) : null;
  const matchDone  = completed.filter(p => ['match','competition'].includes(p.type) && p.result);
  const wins       = matchDone.filter(p => p.result === 'win').length;
  const winRate    = matchDone.length ? Math.round(wins / matchDone.length * 100) : null;
  const trainingCount = allPast.filter(p => TRAINING_TYPES.includes(p.type)).length;
  const avgOf = (field) => {
    const vals = matchDone.filter(p => p[field]).map(p => p[field]);
    return vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : null;
  };
  const avgServe    = avgOf('serve_rating');
  const avgReturn   = avgOf('return_rating');
  const avgForehand = avgOf('forehand_rating');
  const avgBackhand = avgOf('backhand_rating');
  const avgNetPlay  = avgOf('net_play_rating');
  const hasBreakdown = avgServe || avgReturn || avgForehand || avgBackhand || avgNetPlay;
  // Ratings are per-sport in principle, but one profile is one sport (see
  // backlog G) — every match here shares the same sport, so the first one
  // tells us which label/icon the 5th breakdown category should use.
  const breakdownSport = matchDone[0]?.sport;

  const stats = [
    { label: 'Game Plans',  value: allPast.length,                          color: C.text    },
    { label: 'Completed', value: completed.length,                        color: '#10b981' },
    { label: 'Scheduled', value: pending.length,                          color: '#f59e0b' },
    { label: 'Avg Perf',  value: avgPerf  ? `${avgPerf}★`  : '—',        color: C.accent  },
    { label: 'Win rate',  value: winRate  != null ? `${winRate}%` : '—', color: '#a78bfa' },
    { label: 'Trainings', value: trainingCount || '—', color: TRAINING_COLOR },
  ];

  return (
    <div>
      {/* Stats strip — the per-match breakdown (serve/return/etc.) is an
          average over the exact same Game Plans counted above, so this is
          one dataset, not two — rendered as a single bordered card with an
          internal divider between the overview row and the breakdown row,
          instead of two separate floating boxes (user feedback 2026-10-02).
          Any leftover grid cell (5 breakdown stats not dividing evenly into
          3 columns on mobile) gets an explicit dark filler cell rather than
          showing through as a gap, so it doesn't read as missing data. */}
      <div style={{
        border: '1.5px solid rgba(56,189,248,0.65)', borderRadius: 10, overflow: 'hidden', marginBottom: 16,
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 1, background: 'rgba(56,189,248,0.35)',
        }}>
          {stats.map(s => (
            <div key={s.label} style={{ background: '#0f172a', padding: isMobile ? '12px 8px' : '14px 12px', textAlign: 'center' }}>
              <div style={{ color: s.color, fontSize: isMobile ? 18 : 22, fontWeight: 800 }}>{s.value}</div>
              <div style={{ color: C.muted, fontSize: isMobile ? 9 : 11, marginTop: 3 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Match breakdown — serve, return, forehand, backhand and net play averages across logged matches */}
        {hasBreakdown && (() => {
          const breakdownStats = [
            { label: 'Serve',    value: avgServe },
            { label: 'Return',   value: avgReturn },
            { label: 'Forehand', value: avgForehand },
            { label: 'Backhand', value: avgBackhand },
            { label: netPlayLabel(breakdownSport), value: avgNetPlay },
          ];
          const cols = isMobile ? 3 : 5;
          const fillerCount = (cols - (breakdownStats.length % cols)) % cols;
          const cells = [...breakdownStats, ...Array.from({ length: fillerCount }, () => null)];
          return (
            <div style={{
              display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 1,
              background: 'rgba(56,189,248,0.35)', borderTop: '1.5px solid rgba(56,189,248,0.65)',
            }}>
              {cells.map((s, i) => s ? (
                <div key={s.label} style={{ background: '#0f172a', padding: isMobile ? '10px 8px' : '11px 12px', textAlign: 'center' }}>
                  <div style={{ color: C.accent, fontSize: isMobile ? 15 : 17, fontWeight: 800 }}>{s.value ? `${s.value}★` : '—'}</div>
                  <div style={{ color: C.muted, fontSize: isMobile ? 9 : 10.5, marginTop: 3 }}>{s.label}</div>
                </div>
              ) : (
                <div key={`filler-${i}`} style={{ background: '#0f172a' }} />
              ))}
            </div>
          );
        })()}
      </div>

      {/* Form & situation — chart, competition standings, AI development areas,
          and the full match history table (see backlog B + C). */}
      <FormSituationPanel
        plans={plans}
        userId={userId}
        activeSport={activeSport}
        demoMode={demoMode}
        onOpenDetail={onOpenDetail}
        isMobile={isMobile}
      />

      <TrainingsTable
        onOpenDetail={onOpenDetail}
        plans={plans}
        filterSport=""
      />
    </div>
  );
}

// ── Trainings Table ──────────────────────────────────────────────────────────

function TrainingsTable({ plans, filterSport, onOpenDetail }) {
  const isMobile = useIsMobile();
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [filterTime, setFilterTime] = useState('');
  const [filterPerf, setFilterPerf] = useState('');

  const todayStr = today();

  // Duration and avg HR are the player's own optional entries on the plan.
  const resolveAct = (plan) => ({ duration_minutes: plan.actual_duration_minutes ?? null, avg_hr: plan.avg_heart_rate ?? null });

  const rows = useMemo(() => {
    const now = new Date();
    let filtered = plans.filter(p => {
      if ((p.scheduled_date || '').slice(0, 10) > todayStr) return false;
      if (!TRAINING_TYPES.includes(p.type) && p.type !== 'training') return false;
      // exclude match/competition/race_day
      if (['match', 'competition', 'race_day'].includes(p.type)) return false;
      if (filterSport && p.sport !== filterSport) return false;
      if (filterPerf && Math.round(p.performance_score) !== Number(filterPerf)) return false;
      if (filterTime) {
        const d = new Date((p.scheduled_date || '').slice(0, 10) + 'T12:00:00');
        const diff = (now - d) / 86400000;
        if (filterTime === 'month'  && diff > 31)  return false;
        if (filterTime === '3month' && diff > 92)  return false;
        if (filterTime === 'year'   && diff > 365) return false;
      }
      return true;
    });

    const withAct = filtered.map(plan => ({ plan, act: resolveAct(plan) }));
    const val = ({ plan, act }) => {
      if (sortKey === 'date')     return plan.scheduled_date || '';
      if (sortKey === 'duration') return act?.duration_minutes || 0;
      if (sortKey === 'hr')       return act?.avg_hr || 0;
      if (sortKey === 'perf')     return plan.performance_score || 0;
      return '';
    };
    return [...withAct].sort((a, b) => {
      const av = val(a), bv = val(b);
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === 'desc' ? -cmp : cmp;
    });
  }, [plans, filterSport, filterPerf, filterTime, sortKey, sortDir, todayStr]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
  };
  const arrow = (key) => sortKey === key ? (sortDir === 'desc' ? ' ↓' : ' ↑') : '';

  const sel = {
    background: '#1a2236', border: `1px solid ${C.border}`, borderRadius: 7,
    color: C.text, fontSize: 12, padding: '5px 9px', fontFamily: C.font, cursor: 'pointer',
  };

  const cardStyle = {
    background: 'rgba(15,23,42,0.82)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
    border: '1.5px solid rgba(56,189,248,0.65)', borderRadius: 14, padding: '20px 20px 8px', marginBottom: 20,
  };

  const thStyle = (key) => ({
    padding: '6px 10px 10px', textAlign: 'left', fontSize: 10, fontWeight: 700,
    color: key && sortKey === key ? C.accent : C.muted,
    textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap',
    cursor: key ? 'pointer' : 'default', userSelect: 'none',
  });

  return (
    <div style={cardStyle}>
      {/* Header + filters */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flex: 1 }}>
          <h3 style={{ margin: 0, color: C.logoBlue, fontSize: 16, fontWeight: 800, letterSpacing: '-0.3px' }}>
            🏋️ TRAININGS
          </h3>
          <span style={{ fontSize: 12, color: C.muted }}>{rows.length} sessions</span>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <select value={filterTime} onChange={e => setFilterTime(e.target.value)} style={sel}>
            <option value="">All time</option>
            <option value="month">This month</option>
            <option value="3month">Last 3 months</option>
            <option value="year">This year</option>
          </select>
          <select value={filterPerf} onChange={e => setFilterPerf(e.target.value)} style={sel}>
            <option value="">Any perf ★</option>
            {[5,4,3,2,1].map(n => <option key={n} value={n}>{'★'.repeat(n)}{'☆'.repeat(5-n)}</option>)}
          </select>
          {(filterTime || filterPerf) && (
            <button onClick={() => { setFilterTime(''); setFilterPerf(''); }}
              style={{ ...sel, color: '#ef4444', borderColor: 'rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.08)' }}>✕</button>
          )}
        </div>
      </div>

      {rows.length === 0 ? (
        <div style={{ padding: '32px 20px', textAlign: 'center', border: '1px dashed rgba(255,255,255,0.12)', borderRadius: 10, marginBottom: 12 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🏋️</div>
          <div style={{ color: C.text, fontWeight: 700, fontSize: 14, marginBottom: 4 }}>No training sessions yet</div>
          <div style={{ color: C.muted, fontSize: 12 }}>Add Training sessions in Game Plan to see them here.</div>
        </div>
      ) : isMobile ? (
        /* Mobile: cards */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map(({ plan, act }) => (
            <div key={plan.id} style={{
              background: 'rgba(255,255,255,0.03)', border: `1px solid rgba(56,189,248,0.30)`,
              borderRadius: 10, padding: '10px 12px',
            }}>
              <div
                onClick={onOpenDetail ? () => onOpenDetail(plan) : undefined}
                style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, cursor: onOpenDetail ? 'pointer' : 'default' }}
              >
                <span style={{ fontSize: 16 }}>{SPORT_EMOJIS[plan.sport] || '🏋️'}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: onOpenDetail ? C.accent : C.text, textTransform: 'capitalize', textDecoration: onOpenDetail ? 'underline' : 'none', textDecorationColor: 'rgba(52,211,153,0.35)' }}>{plan.sport || 'Training'}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>{fmtDate(plan.scheduled_date?.slice(0,10))}</div>
                </div>
                {plan.performance_score > 0 && (
                  <span style={{ color: '#f59e0b', fontSize: 12 }}>{'★'.repeat(plan.performance_score)}{'☆'.repeat(5-plan.performance_score)}</span>
                )}
              </div>
              {plan.goal && <div style={{ fontSize: 11, color: C.muted, marginBottom: 4, fontStyle: 'italic' }}>{plan.goal}</div>}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 11 }}>
                {act?.duration_minutes && <span style={{ color: C.muted }}>⏱ {act.duration_minutes}m</span>}
                {act?.avg_hr && <span style={{ color: C.muted }}>❤️ {Math.round(act.avg_hr)} bpm</span>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Desktop: table */
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, color: C.text }}>
            <thead>
              <tr style={{ borderBottom: `1px solid rgba(56,189,248,0.45)` }}>
                {[
                  { label: 'Date',     key: 'date'     },
                  { label: 'Sport',    key: null        },
                  { label: 'Goal',     key: null        },
                  { label: 'Duration', key: 'duration' },
                  { label: 'HR',       key: 'hr'       },
                  { label: 'Perf.',    key: 'perf'     },
                ].map(({ label, key }) => (
                  <th key={label} onClick={key ? () => toggleSort(key) : undefined} style={thStyle(key)}>
                    {label}{key ? arrow(key) : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(({ plan, act }) => (
                <tr key={plan.id} style={{ borderBottom: `1px solid rgba(56,189,248,0.30)` }}>
                  <td style={{ padding: '11px 10px', whiteSpace: 'nowrap', color: C.muted, fontSize: 12 }}>
                    {fmtDate(plan.scheduled_date?.slice(0, 10))}
                  </td>
                  <td style={{ padding: '11px 10px', minWidth: 100 }}>
                    <div
                      onClick={onOpenDetail ? () => onOpenDetail(plan) : undefined}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: onOpenDetail ? 'pointer' : 'default' }}
                    >
                      <span>{SPORT_EMOJIS[plan.sport] || '🏋️'}</span>
                      <span style={{ fontWeight: 600, fontSize: 13, textTransform: 'capitalize', color: onOpenDetail ? C.accent : 'inherit', textDecoration: onOpenDetail ? 'underline' : 'none', textDecorationColor: 'rgba(52,211,153,0.35)' }}>{plan.sport || 'Training'}</span>
                    </div>
                  </td>
                  <td style={{ padding: '11px 10px', maxWidth: 260, color: C.muted, fontSize: 12 }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {plan.goal || '—'}
                    </div>
                  </td>
                  <td style={{ padding: '11px 10px', fontSize: 12, color: C.text }}>
                    {act?.duration_minutes ? `${act.duration_minutes}m` : '—'}
                  </td>
                  <td style={{ padding: '11px 10px', fontSize: 12, color: C.text }}>{act?.avg_hr || '—'}</td>
                  <td style={{ padding: '11px 10px' }}>
                    {plan.performance_score > 0
                      ? <span style={{ fontSize: 13, letterSpacing: 1 }}>
                          {[1,2,3,4,5].map(n => <span key={n} style={{ color: n <= plan.performance_score ? '#f59e0b' : C.border }}>★</span>)}
                        </span>
                      : <span style={{ color: C.muted, fontSize: 12 }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Clickable chart legend — click a series name to pick it out of a crowded
// chart. First click shows only that line; clicking another one adds it
// alongside (so you can build up a small comparison, e.g. backhand +
// forehand + serve); clicking a shown one again removes it; once nothing is
// picked anymore, every line is back ("Show all" does the same in one go).
// Plain recharts <Legend> only labels the lines, it can't do any of this.
function ClickableLegend({ payload, selected, onToggle, onShowAll }) {
  if (!payload?.length) return null;
  const hasSelection = selected.length > 0;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '6px 14px', paddingTop: 8 }}>
      {payload.map(entry => {
        const isPicked = selected.includes(entry.dataKey);
        const dimmed = hasSelection && !isPicked;
        return (
          <button
            key={entry.dataKey}
            onClick={() => onToggle(entry.dataKey)}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              background: isPicked ? `${entry.color}22` : 'transparent',
              border: `1px solid ${isPicked ? entry.color : 'transparent'}`,
              borderRadius: 6, padding: '2px 7px', cursor: 'pointer',
              opacity: dimmed ? 0.4 : 1, transition: 'opacity 0.15s, background 0.15s',
            }}
            title={isPicked ? `Click to hide ${entry.value}` : `Click to show ${entry.value}`}
          >
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: entry.color, flexShrink: 0 }} />
            <span style={{ fontSize: 11, color: dimmed ? '#6b7a8a' : '#a8b3bd', fontWeight: isPicked ? 700 : 400 }}>
              {entry.value}
            </span>
          </button>
        );
      })}
      {hasSelection && (
        <button
          onClick={onShowAll}
          style={{ background: 'none', border: 'none', color: '#38bdf8', fontSize: 11, cursor: 'pointer', textDecoration: 'underline', padding: '2px 4px' }}
        >
          Show all
        </button>
      )}
    </div>
  );
}

// ── Analytics View ───────────────────────────────────────────────────────────

function AnalyticsView({ plans, onOpenDetail }) {
  const isMobile = useIsMobile();
  const todayStr = today();

  // ── Filter state ──────────────────────────────────────────────────────────
  const [fSport,    setFSport]    = useState('all');
  const [fType,     setFType]     = useState('all');
  const [fOpponent, setFOpponent] = useState('all');
  const [fIntensity,setFIntensity]= useState('all');
  const [fResult,   setFResult]   = useState('all');
  const [fMinPerf,  setFMinPerf]  = useState(0);
  const [fMinAdh,   setFMinAdh]   = useState(0);
  const [fRange,    setFRange]    = useState('all');

  // Which series are picked per chart via the clickable legend — an empty
  // array means all lines show, same as before this was added.
  const [perfPicked,      setPerfPicked]      = useState([]);
  const [breakdownPicked, setBreakdownPicked] = useState([]);
  const togglePick = setter => key => setter(cur => cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key]);

  // Combined dataset: past plans with their own logged data
  const dataset = useMemo(() => {
    return plans
      .filter(p => (p.scheduled_date || '').slice(0, 10) <= todayStr && (p.performance_score || p.game_plan_adherence))
      .map(p => {
        return {
          id: p.id,
          date: p.scheduled_date?.slice(0, 10),
          dateLabel: fmtDate(p.scheduled_date?.slice(0, 10)),
          perf: p.performance_score || null,
          adh: p.game_plan_adherence || null,
          serve: p.serve_rating || null,
          ret: p.return_rating || null,
          fh: p.forehand_rating || null,
          bh: p.backhand_rating || null,
          np: p.net_play_rating || null,
          sport: (p.sport || '').toLowerCase(),
          type: p.type || 'training',
          result: p.result || null,
          goal: p.goal || null,
          opponent: p.opponent_name || null,
          intensity: p.intensity_level || null,
        };
      })
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [plans, todayStr]); // eslint-disable-line react-hooks/exhaustive-deps

  // Maps a dataset row's id back to the real plan object — dataset rows are
  // a lightweight reshaping for the charts/table (date/perf/adh/...), not
  // the full plan shape onOpenDetail (SessionDetailModal/SessionModal)
  // expects, so a row click must resolve back to the original.
  const plansById = useMemo(() => new Map(plans.map(p => [p.id, p])), [plans]);

  // ── Dynamic filter options from data ────────────────────────────────────
  const sportOptions    = useMemo(() => [...new Set(dataset.map(d => d.sport).filter(Boolean))].sort(), [dataset]);
  const opponentOptions = useMemo(() => [...new Set(dataset.map(d => d.opponent).filter(Boolean))].sort(), [dataset]);
  const hasMatches      = useMemo(() => dataset.some(d => d.type === 'match' || d.opponent), [dataset]);

  // ── Apply filters ────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const cutoff = fRange === 'all' ? null
      : fRange === '30d'  ? toLocalISO(new Date(Date.now() - 30  * 86400000))
      : fRange === '90d'  ? toLocalISO(new Date(Date.now() - 90  * 86400000))
      : fRange === '6m'   ? toLocalISO(new Date(Date.now() - 183 * 86400000))
      : null;
    return dataset.filter(d => {
      if (fSport    !== 'all' && d.sport    !== fSport)    return false;
      if (fType     !== 'all' && d.type     !== fType)     return false;
      if (fOpponent !== 'all' && d.opponent !== fOpponent) return false;
      if (fIntensity!== 'all' && String(d.intensity) !== fIntensity) return false;
      if (fResult   !== 'all') {
        const r = (d.result || '').toLowerCase();
        if (fResult === 'win'     && !r.startsWith('win'))     return false;
        if (fResult === 'loss'    && !r.startsWith('loss'))    return false;
        if (fResult === 'draw'    && !r.startsWith('draw'))    return false;
        if (fResult === 'success' && !r.includes('success'))   return false;
        if (fResult === 'partial' && !r.includes('partial'))   return false;
        if (fResult === 'fail'    && !r.includes('fail'))      return false;
      }
      if (fMinPerf  > 0 && (d.perf  || 0) < fMinPerf) return false;
      if (fMinAdh   > 0 && (d.adh   || 0) < fMinAdh)  return false;
      if (cutoff && d.date < cutoff) return false;
      return true;
    });
  }, [dataset, fSport, fType, fOpponent, fIntensity, fResult, fMinPerf, fMinAdh, fRange]);

  const cardStyle = {
    background: 'rgba(15,23,42,0.82)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
    border: '1.5px solid rgba(56,189,248,0.65)', borderRadius: 14,
    padding: '20px 20px 16px', marginBottom: 20,
  };

  const chartH = isMobile ? 220 : 280;

  const SPORT_ICON = { running:'🏃', cycling:'🚴', swimming:'🏊', tennis:'🎾', padel:'🏸', strength:'🏋️', triathlon:'🏅' };

  const LineTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    const stars = (n) => n ? '★'.repeat(n) + '☆'.repeat(5 - n) : null;
    const icon = SPORT_ICON[d?.sport] || '🎯';
    return (
      <div style={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, padding: '10px 14px', fontSize: 12, minWidth: 180 }}>
        <div style={{ color: '#94a3b8', marginBottom: 6, fontWeight: 700 }}>{icon} {label}</div>
        {d?.sport && <div style={{ color: C.muted, fontSize: 11, marginBottom: 6, textTransform: 'capitalize' }}>{d.sport}{d.type && d.type !== d.sport ? ` · ${d.type}` : ''}</div>}
        {payload.map((p, i) => (
          <div key={i} style={{ color: p.color, marginBottom: 2 }}>
            {p.name}: <strong>{p.name.includes('★') ? (stars(p.value) || '—') : p.value}</strong>
          </div>
        ))}
        {d?.result && (
          <div style={{ color: '#93c5fd', fontSize: 11, marginTop: 6, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 6, lineHeight: 1.5 }}>
            📝 {d.result.length > 120 ? d.result.slice(0, 120) + '…' : d.result}
          </div>
        )}
        {d?.goal && !d?.result && (
          <div style={{ color: C.muted, fontSize: 11, marginTop: 6, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 6 }}>
            🎯 {d.goal.length > 100 ? d.goal.slice(0, 100) + '…' : d.goal}
          </div>
        )}
      </div>
    );
  };

  if (dataset.length < 2) return (
    <div style={{ ...cardStyle, textAlign: 'center', padding: '48px 24px' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>📈</div>
      <div style={{ color: C.text, fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Not enough data yet</div>
      <div style={{ color: C.muted, fontSize: 13 }}>Log at least 2 sessions with performance ratings to see charts.</div>
    </div>
  );

  // ── Derived chart datasets from filtered ────────────────────────────────
  const lineData    = filtered.filter(d => d.perf || d.adh);
  const breakdownData = filtered.filter(d => d.serve || d.ret || d.fh || d.bh || d.np);
  const breakdownSport = breakdownData[0]?.sport;

  // ── Helper UI ─────────────────────────────────────────────────────────
  const pill = (active, onClick, label) => (
    <button key={label} onClick={onClick} style={{
      background: active ? C.accent : 'rgba(255,255,255,0.06)',
      color: active ? '#0a0f1a' : C.muted,
      border: 'none', borderRadius: 20, padding: '4px 12px',
      fontSize: 11, fontWeight: active ? 700 : 400, cursor: 'pointer', whiteSpace: 'nowrap',
    }}>{label}</button>
  );

  const drop = (value, onChange, options, placeholder) => (
    <select value={value} onChange={e => onChange(e.target.value)} style={{
      background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
      color: value !== 'all' && value !== '0' ? C.text : C.muted,
      borderRadius: 8, padding: '4px 10px', fontSize: 11, cursor: 'pointer',
    }}>
      <option value="all">{placeholder}</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );

  const sectionTitle = (title, sub) => (
    <div style={{ marginBottom: 16 }}>
      <div style={{ color: C.logoBlue, fontSize: 14, fontWeight: 700 }}>{title}</div>
      {sub && <div style={{ color: C.muted, fontSize: 11, marginTop: 2 }}>{sub}</div>}
    </div>
  );

  return (
    <div>
      {/* ── Filters ── */}
      <div style={{ ...cardStyle, padding: '14px 16px', marginBottom: 16 }}>
        {/* Row 1: Time range + Type */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10, alignItems: 'center' }}>
          <span style={{ color: C.muted, fontSize: 11, marginRight: 2 }}>Time:</span>
          {[['all','All time'],['30d','30d'],['90d','90d'],['6m','6m']].map(([v,l]) => pill(fRange===v, ()=>setFRange(v), l))}
          <div style={{ width: 1, height: 16, background: 'rgba(255,255,255,0.10)', margin: '0 4px' }} />
          <span style={{ color: C.muted, fontSize: 11, marginRight: 2 }}>Type:</span>
          {[['all','All'],['training','Training'],['match','Match'],['competition','Competition']].map(([v,l]) => pill(fType===v, ()=>setFType(v), l))}
        </div>
        {/* Row 2: Dropdowns */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          {drop(fSport, setFSport,
            sportOptions.map(s => ({ value: s, label: `${SPORT_ICON[s]||'🎯'} ${s.charAt(0).toUpperCase()+s.slice(1)}` })),
            'All sports')}
          {[['all','All intensity'],['1','Intensity 1'],['2','Intensity 2'],['3','Intensity 3'],['4','Intensity 4'],['5','Intensity 5']].slice(1).length > 0 &&
            drop(fIntensity, setFIntensity,
              ['1','2','3','4','5'].map(v => ({ value: v, label: `Intensity ${v}` })),
              'Any intensity')}
          {drop(fResult, setFResult,
            [{ value:'win', label:'Win' },{ value:'loss', label:'Loss' },{ value:'draw', label:'Draw' }],
            'Any result')}
          {hasMatches && drop(fOpponent, setFOpponent,
            opponentOptions.map(o => ({ value: o, label: `vs ${o}` })),
            'Any opponent')}
          {drop(fMinPerf, v => setFMinPerf(Number(v)),
            [{ value:'2', label:'Perf ≥ 2★' },{ value:'3', label:'Perf ≥ 3★' },{ value:'4', label:'Perf ≥ 4★' }],
            'Any perf ★')}
          {drop(fMinAdh, v => setFMinAdh(Number(v)),
            [{ value:'2', label:'Plan outcome ≥ 2★' },{ value:'3', label:'Plan outcome ≥ 3★' },{ value:'4', label:'Plan outcome ≥ 4★' }],
            'Any plan outcome ★')}
        </div>
        {/* Session count */}
        <div style={{ color: C.muted, fontSize: 11, marginTop: 10 }}>
          {filtered.length} session{filtered.length !== 1 ? 's' : ''} match your filters
          {filtered.length !== dataset.length && (
            <button onClick={() => { setFSport('all'); setFType('all'); setFOpponent('all'); setFIntensity('all'); setFResult('all'); setFMinPerf(0); setFMinAdh(0); setFRange('all'); }}
              style={{ marginLeft: 10, background: 'none', border: 'none', color: C.accent, fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}>
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* ── Chart 1: Performance + Plan outcome over time ── */}
      <div style={cardStyle}>
        {sectionTitle('📈 Performance & Plan Outcome over time', '★ rating (1–5)')}
        {lineData.length < 2 ? (
          <div style={{ color: C.muted, fontSize: 13, textAlign: 'center', padding: '20px 0' }}>Not enough session data for current filters.</div>
        ) : (
          <ResponsiveContainer width="100%" height={chartH}>
            <LineChart data={lineData} margin={{ top: 4, right: 12, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="dateLabel" tick={{ fill: C.muted, fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis domain={[0, 5]} ticks={[1,2,3,4,5]} tickFormatter={v => `${v}★`} tick={{ fill: C.muted, fontSize: 10 }} tickLine={false} axisLine={false} width={32} />
              {/* Tooltip disabled on mobile (user feedback, 2026-10-30):
                  recharts' hover tooltip is driven by touch events that
                  don't have a matching "touch end" to dismiss it the way a
                  mouse's mouseleave does, so on a phone it sticks open over
                  the chart until something else is tapped — cluttering
                  exactly the view it's meant to clarify. Desktop keeps it. */}
              {!isMobile && <Tooltip content={<LineTooltip />} />}
              <Legend content={<ClickableLegend selected={perfPicked} onToggle={togglePick(setPerfPicked)} onShowAll={() => setPerfPicked([])} />} />
              <Line type="monotone" dataKey="perf" name="Performance ★" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3, fill: '#f59e0b' }} connectNulls hide={perfPicked.length > 0 && !perfPicked.includes('perf')} />
              <Line type="monotone" dataKey="adh" name="Plan outcome ★" stroke={C.accent} strokeWidth={2} dot={{ r: 3, fill: C.accent }} connectNulls hide={perfPicked.length > 0 && !perfPicked.includes('adh')} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* ── Chart 2: Serve / Return / Forehand / Backhand / Net play (or Short game) over time ── */}
      <div style={cardStyle}>
        {sectionTitle(`🎾 Serve, return, forehand, backhand & ${netPlayLabel(breakdownSport).toLowerCase()} over time`, 'Each part of your game, rated ★ (1–5) after each match')}
        {breakdownData.length < 2 ? (
          <div style={{ color: C.muted, fontSize: 13, textAlign: 'center', padding: '20px 0' }}>Rate your serve, return, forehand, backhand and {netPlayLabel(breakdownSport).toLowerCase()} on at least 2 matches to see the trend.</div>
        ) : (
          <ResponsiveContainer width="100%" height={chartH}>
            <LineChart data={breakdownData} margin={{ top: 4, right: 12, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="dateLabel" tick={{ fill: C.muted, fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis domain={[0, 5]} ticks={[1,2,3,4,5]} tickFormatter={v => `${v}★`} tick={{ fill: C.muted, fontSize: 10 }} tickLine={false} axisLine={false} width={32} />
              {!isMobile && <Tooltip content={<LineTooltip />} />}
              <Legend content={<ClickableLegend selected={breakdownPicked} onToggle={togglePick(setBreakdownPicked)} onShowAll={() => setBreakdownPicked([])} />} />
              <Line type="monotone" dataKey="serve" name="Serve ★" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3, fill: '#f59e0b' }} connectNulls hide={breakdownPicked.length > 0 && !breakdownPicked.includes('serve')} />
              <Line type="monotone" dataKey="ret" name="Return ★" stroke="#38bdf8" strokeWidth={2} dot={{ r: 3, fill: '#38bdf8' }} connectNulls hide={breakdownPicked.length > 0 && !breakdownPicked.includes('ret')} />
              <Line type="monotone" dataKey="fh" name="Forehand ★" stroke="#a78bfa" strokeWidth={2} dot={{ r: 3, fill: '#a78bfa' }} connectNulls hide={breakdownPicked.length > 0 && !breakdownPicked.includes('fh')} />
              <Line type="monotone" dataKey="bh" name="Backhand ★" stroke="#34d399" strokeWidth={2} dot={{ r: 3, fill: '#34d399' }} connectNulls hide={breakdownPicked.length > 0 && !breakdownPicked.includes('bh')} />
              <Line type="monotone" dataKey="np" name={`${netPlayLabel(breakdownSport)} ★`} stroke="#f472b6" strokeWidth={2} dot={{ r: 3, fill: '#f472b6' }} connectNulls hide={breakdownPicked.length > 0 && !breakdownPicked.includes('np')} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* ── Sessions behind the charts above — user feedback 2026-10-30:
          "47 sessions match your filters" was just a count, with no way to
          see which game plans that actually meant. Same rows as the
          charts — same `filtered` dataset, same filters — so whether
          trainings show here is already controlled by the existing
          Type: All/Training/Match/Competition filter above; no separate
          match-only rule needed (user feedback, same day: the Type filter
          already does this job). Newest first, capped to a ~10-row scroll
          window so the table can't push the page long. ── */}
      {filtered.length > 0 && (
        <div style={cardStyle}>
          {sectionTitle('📋 Sessions in this view', `${filtered.length} session${filtered.length !== 1 ? 's' : ''} matching your filters`)}
          <div style={{ maxHeight: 280, overflowY: 'auto' }}>
            {[...filtered].sort((a, b) => b.date.localeCompare(a.date)).map(d => {
              const resultColor = d.result?.startsWith('win') ? '#22c55e' : d.result?.startsWith('loss') ? '#ef4444' : d.result?.startsWith('draw') ? '#eab308' : C.muted;
              return (
                <div key={d.id} onClick={() => onOpenDetail && onOpenDetail(plansById.get(d.id))} role="button" tabIndex={0} onKeyDown={onEnterOrSpace(() => onOpenDetail && onOpenDetail(plansById.get(d.id)))} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '7px 2px',
                  borderTop: `1px solid rgba(255,255,255,0.06)`, cursor: onOpenDetail ? 'pointer' : 'default',
                }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: d.type === 'match' ? resultColor : TRAINING_COLOR, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: C.text, fontSize: 12.5, fontWeight: 600 }}>
                    {d.opponent ? `vs ${d.opponent}` : (d.goal || d.sport)}
                  </div>
                  <div style={{ color: C.muted, fontSize: 10.5, flexShrink: 0 }}>{d.dateLabel}</div>
                  {d.perf && <div style={{ color: '#f59e0b', fontSize: 11, flexShrink: 0 }}>{d.perf}★</div>}
                  {d.type === 'match' ? (
                    <div style={{ color: resultColor, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', flexShrink: 0, width: 34, textAlign: 'right' }}>{d.result || '—'}</div>
                  ) : (
                    <div style={{ color: TRAINING_COLOR, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', flexShrink: 0, width: 54, textAlign: 'right' }}>Training</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Notes Library ────────────────────────────────────────────────────────────

const SPORT_ICONS_LIB = {
  running:'🏃', cycling:'🚴', swimming:'🏊', tennis:'🎾', padel:'🏸',
  strength:'🏋️', hiking:'🥾', triathlon:'🏅', football:'⚽', basketball:'🏀',
  volleyball:'🏐', hockey:'🏒', golf:'⛳', crossfit:'💪', yoga:'🧘',
  trail_running:'🏔️', mountain_biking:'🚵', boxing:'🥊', mma:'🥋',
  judo:'🥋', fencing:'🤺', climbing:'🧗', rowing:'🚣', skiing:'⛷️',
  snowboarding:'🏂', other:'📊',
};

function NotesLibraryView({ plans }) {
  const isMobile = useIsMobile();
  const [query,        setQuery]        = useState('');
  const [filterSport,  setFilterSport]  = useState('');
  const [filterType,   setFilterType]   = useState('');
  const [filterResult, setFilterResult] = useState('');
  const [filterTime,   setFilterTime]   = useState('');
  const [filterPerf,   setFilterPerf]   = useState('');

  const todayStr = today();

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    const now = new Date();
    return plans
      .filter(p => {
        // past only + must have something written
        if (p.scheduled_date > todayStr) return false;
        if (!p.result && !p.performance_score && !p.goal && !p.game_plan && !p.post_notes) return false;
        // text search
        if (q && ![ p.goal, p.game_plan, p.post_notes, p.opponent_name ].some(f => (f||'').toLowerCase().includes(q))) return false;
        // sport
        if (filterSport && p.sport !== filterSport) return false;
        // type
        if (filterType && p.type !== filterType) return false;
        // result
        if (filterResult && p.result !== filterResult) return false;
        // time
        if (filterTime) {
          const d = new Date(p.scheduled_date + 'T12:00:00');
          const diffDays = (now - d) / 86400000;
          if (filterTime === 'month'  && diffDays > 31)  return false;
          if (filterTime === '3month' && diffDays > 92)  return false;
          if (filterTime === 'year'   && diffDays > 365) return false;
        }
        // perf stars
        if (filterPerf && Math.round(p.performance_score) !== Number(filterPerf)) return false;
        return true;
      })
      .sort((a,b) => b.scheduled_date.localeCompare(a.scheduled_date));
  }, [plans, query, filterSport, filterType, filterResult, filterTime, filterPerf, todayStr]);

  const sportOptions = useMemo(() => {
    const seen = new Set();
    return plans.filter(p => p.sport && seen.size !== seen.add(p.sport).size).map(p => p.sport).sort();
  }, [plans]);

  const resultColor = r => r === 'win' ? '#10b981' : r === 'draw' ? '#f59e0b' : r === 'loss' ? '#ef4444' : C.muted;
  const typeLabel   = t => t === 'training' ? 'Training' : t === 'match' ? 'Match' : t === 'competition' ? 'Competition' : t === 'race_day' ? 'Race' : t === 'brick' ? 'Brick' : t;
  const typeColor   = t => t === 'match' ? '#ef4444' : t === 'competition' ? '#f59e0b' : t === 'race_day' ? '#f97316' : C.muted;

  const selStyle = {
    background: '#1a2236', border: `1px solid ${C.border}`, borderRadius: 7,
    color: C.text, fontSize: 12, padding: '6px 10px', fontFamily: C.font, cursor: 'pointer',
    flex: isMobile ? '1 1 calc(50% - 4px)' : 'unset',
  };

  return (
    <div>
      {/* Search bar */}
      <input
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder="🔍  Search goals, plans, notes, opponents…"
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: '10px 14px', borderRadius: 9, marginBottom: 10,
          background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`,
          color: C.text, fontSize: 13, fontFamily: C.font, outline: 'none',
        }}
      />

      {/* Filter row */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        <select value={filterSport} onChange={e => setFilterSport(e.target.value)} style={selStyle}>
          <option value="">All sports</option>
          {sportOptions.map(s => <option key={s} value={s}>{SPORT_ICONS_LIB[s] || '📊'} {s}</option>)}
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)} style={selStyle}>
          <option value="">All types</option>
          <option value="training">Training</option>
          <option value="match">Match</option>
          <option value="competition">Competition</option>
          <option value="race_day">Race</option>
        </select>
        <select value={filterResult} onChange={e => setFilterResult(e.target.value)} style={selStyle}>
          <option value="">Any result</option>
          <option value="win">Win</option>
          <option value="draw">Draw</option>
          <option value="loss">Loss</option>
          <option value="achieved">Achieved</option>
          <option value="not_achieved">Not achieved</option>
        </select>
        <select value={filterTime} onChange={e => setFilterTime(e.target.value)} style={selStyle}>
          <option value="">All time</option>
          <option value="month">This month</option>
          <option value="3month">Last 3 months</option>
          <option value="year">This year</option>
        </select>
        <select value={filterPerf} onChange={e => setFilterPerf(e.target.value)} style={selStyle}>
          <option value="">Any perf ★</option>
          {[5,4,3,2,1].map(n => <option key={n} value={n}>{'★'.repeat(n)}{'☆'.repeat(5-n)}</option>)}
        </select>
        {(query||filterSport||filterType||filterResult||filterTime||filterPerf) && (
          <button onClick={() => { setQuery(''); setFilterSport(''); setFilterType(''); setFilterResult(''); setFilterTime(''); setFilterPerf(''); }}
            style={{ ...selStyle, color: '#ef4444', borderColor: 'rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.08)' }}>
            ✕ Clear
          </button>
        )}
      </div>

      <div style={{ color: C.muted, fontSize: 11, marginBottom: 14 }}>
        {filtered.length} session{filtered.length !== 1 ? 's' : ''}
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: C.muted, fontSize: 13 }}>
          No game plans match your filters.
        </div>
      ) : (
        filtered.map(p => {
          const icon = SPORT_ICONS_LIB[p.sport] || '📊';
          const perf = p.performance_score;
          const planAdh = p.game_plan_adherence;
          const rc = resultColor(p.result);
          const label = (key, val) => val ? (
            <div style={{ marginBottom: 5 }}>
              <div style={{ color: C.muted, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>{key}</div>
              <div style={{ color: C.text, fontSize: 12, lineHeight: 1.5 }}>{val}</div>
            </div>
          ) : null;
          return (
            <div key={p.id} style={{
              background: 'rgba(255,255,255,0.03)', border: `1px solid rgba(56,189,248,0.30)`,
              borderRadius: 10, padding: '14px 16px', marginBottom: 10,
            }}>
              {/* Header row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                <span style={{ color: C.muted, fontSize: 12, minWidth: 52 }}>{fmtDate(p.scheduled_date?.slice(0,10))}</span>
                <span style={{ fontSize: 15 }}>{icon}</span>
                <span style={{ color: C.text, fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>{p.sport || '—'}</span>
                <span style={{ color: typeColor(p.type), fontSize: 11, fontWeight: 700,
                  background: `${typeColor(p.type)}18`, borderRadius: 5, padding: '2px 7px' }}>
                  {typeLabel(p.type)}
                </span>
                {p.opponent_name && (
                  <span style={{ color: C.muted, fontSize: 11 }}>vs <strong style={{ color: C.text }}>{opponentLabel(p)}</strong></span>
                )}
                {p.race_name && (
                  <span style={{ color: C.muted, fontSize: 11 }}><strong style={{ color: C.text }}>{p.race_name}</strong></span>
                )}
              </div>

              {/* Two-column body — stacks on mobile */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 12 : 16 }}>
                {/* Left: pre-session intent */}
                <div style={{ borderRight: isMobile ? 'none' : `1px solid ${C.border}`, borderBottom: isMobile ? `1px solid ${C.border}` : 'none', paddingRight: isMobile ? 0 : 16, paddingBottom: isMobile ? 12 : 0 }}>
                  <div style={{ color: C.muted, fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.07em', marginBottom: 8 }}>Game Plan</div>
                  {label('Goal', p.goal)}
                  {label('Focus', p.focus)}
                  {label('Game plan', p.game_plan)}
                  {!p.goal && !p.focus && !p.game_plan && (
                    <span style={{ color: C.muted, fontSize: 12 }}>—</span>
                  )}
                </div>

                {/* Right: post-session reality */}
                <div>
                  <div style={{ color: C.muted, fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.07em', marginBottom: 8 }}>Result</div>
                  {p.result && (
                    <div style={{ marginBottom: 6 }}>
                      <span style={{ color: rc, fontWeight: 800, fontSize: 13,
                        background: `${rc}18`, borderRadius: 5, padding: '2px 9px' }}>
                        {p.result.toUpperCase()}
                      </span>
                      {p.home_away && (() => {
                        const cfg = { home: { label: 'Home', color: '#10b981' }, away: { label: 'Away', color: '#f97316' } }[p.home_away];
                        return cfg ? (
                          <span style={{ color: cfg.color, fontSize: 11, fontWeight: 600, marginLeft: 8 }}>{cfg.label}</span>
                        ) : null;
                      })()}
                      {p.match_score && (() => {
                        const ms = p.match_score;
                        let scoreStr = null;
                        if (ms.sets) {
                          const sets = ms.sets.filter(s => s.me !== '' && s.opp !== '');
                          if (sets.length) scoreStr = sets.map(s => `${s.me}-${s.opp}`).join(', ');
                        } else if (ms.me !== undefined) {
                          scoreStr = `${ms.me} — ${ms.opp}`;
                        }
                        return scoreStr ? <span style={{ color: C.muted, fontSize: 12, marginLeft: 8 }}>{scoreStr}</span> : null;
                      })()}
                    </div>
                  )}
                  {perf > 0 && (
                    <div style={{ marginBottom: 4 }}>
                      <div style={{ color: C.muted, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>Performance</div>
                      <span style={{ color: C.accent, fontSize: 13 }}>{'★'.repeat(perf)}{'☆'.repeat(5-perf)}</span>
                    </div>
                  )}
                  {planAdh > 0 && (
                    <div style={{ marginBottom: 4 }}>
                      <div style={{ color: C.muted, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>Plan outcome</div>
                      <span style={{ color: C.purple, fontSize: 13 }}>{'★'.repeat(planAdh)}{'☆'.repeat(5-planAdh)}</span>
                    </div>
                  )}
                  {!p.result && !perf && !planAdh && (
                    <span style={{ color: C.muted, fontSize: 12 }}>—</span>
                  )}
                </div>
              </div>

              {/* Post notes — full width */}
              {p.post_notes && (
                <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 12, paddingTop: 10 }}>
                  <div style={{ color: C.muted, fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.06em', marginBottom: 4 }}>Post-session notes</div>
                  <div style={{ color: '#93c5fd', fontSize: 12, fontStyle: 'italic', lineHeight: 1.6 }}>{p.post_notes}</div>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

// ── Competitions — leagues, tournaments, practice goals ────────────────────────

function competitionIconGP(type) {
  return type === 'league' ? '🏅' : type === 'tournament' ? '🏆' : '🎯';
}

function CompetitionDetail({ competition, plans, onBack, onOpenDetail }) {
  const linked = plans
    .filter(p => p.competition_id === competition.id)
    .sort((a, b) => (a.scheduled_date || '').localeCompare(b.scheduled_date || ''));

  const matches = linked.filter(p => ['match', 'competition'].includes(p.type) && p.result);
  const w = matches.filter(p => p.result === 'win').length;
  const d = matches.filter(p => p.result === 'draw').length;
  const l = matches.filter(p => p.result === 'loss').length;

  // Season summary — how the competition has gone beyond just W/D/L: games/
  // points won-lost (summed across every set of every match) and the
  // athlete's own average ratings within this competition specifically,
  // since a season can look very different from the all-time averages.
  const games = matches.reduce((acc, p) => {
    const sets = p.match_score?.sets;
    if (Array.isArray(sets)) {
      sets.forEach(s => {
        const me = Number(s.me), opp = Number(s.opp);
        if (!isNaN(me))  acc.me  += me;
        if (!isNaN(opp)) acc.opp += opp;
      });
    }
    return acc;
  }, { me: 0, opp: 0 });
  const hasGames = games.me > 0 || games.opp > 0;

  const avgOf = (field) => {
    const vals = matches.filter(p => p[field]).map(p => p[field]);
    return vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : null;
  };
  const breakdownSport = matches[0]?.sport;
  const ratingTiles = [
    { label: 'Serve',    value: avgOf('serve_rating') },
    { label: 'Return',   value: avgOf('return_rating') },
    { label: 'Forehand', value: avgOf('forehand_rating') },
    { label: 'Backhand', value: avgOf('backhand_rating') },
    { label: netPlayLabel(breakdownSport), value: avgOf('net_play_rating') },
    { label: 'Plan outcome', value: avgOf('game_plan_adherence') },
  ].filter(t => t.value != null);

  // Group by round_label — sessions without a round fall into "Preparation"
  const groups = {};
  linked.forEach(p => {
    const key = p.round_label || (['match', 'competition'].includes(p.type) ? 'Unlabeled' : 'Preparation');
    if (!groups[key]) groups[key] = [];
    groups[key].push(p);
  });

  const isUpcoming = competition.end_date && competition.end_date >= today();
  const daysLeft = competition.end_date ? Math.ceil((new Date(competition.end_date + 'T12:00:00') - new Date()) / 86400000) : null;

  // Progress, shaped by the competition's own format — a league is measured
  // by how many of its planned matches have been played, a tournament by
  // where the athlete sits in its bracket (even before the next match is
  // actually scheduled, if expected_rounds was set up front).
  const totalPlanned = competition.format_meta?.total_matches_planned;
  const leagueProgress = competition.type === 'league' && totalPlanned
    ? `${matches.length}/${totalPlanned} played`
    : null;

  const expectedRounds = competition.format_meta?.expected_rounds || [];
  const playedRounds = new Set(linked.filter(p => p.round_label).map(p => p.round_label));
  const nextRound = expectedRounds.find(r => !playedRounds.has(r));
  const tournamentProgress = competition.type === 'tournament' && expectedRounds.length > 0
    ? (nextRound ? `Next: ${nextRound}` : 'All expected rounds played')
    : null;

  return (
    <div>
      <button onClick={onBack} style={{
        background: 'none', border: 'none', color: C.accent, fontSize: 12.5,
        cursor: 'pointer', fontFamily: C.font, padding: 0, marginBottom: 14,
      }}>← All competitions</button>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
        <div>
          <div style={{ color: C.logoBlue, fontSize: 17, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {competitionIconGP(competition.type)} {competition.name}
            {competition.goal_status === 'achieved' && (
              <span style={{ color: '#22c55e', fontSize: 11, fontWeight: 700, border: '1px solid #22c55e', borderRadius: 20, padding: '2px 9px' }}>✓ Goal achieved</span>
            )}
            {competition.goal_status === 'not_achieved' && (
              <span style={{ color: '#ef4444', fontSize: 11, fontWeight: 700, border: '1px solid #ef4444', borderRadius: 20, padding: '2px 9px' }}>✗ Goal not achieved</span>
            )}
          </div>
          <div style={{ color: C.muted, fontSize: 12, marginTop: 3 }}>
            {competition.type}
            {competition.start_date ? ` · ${fmtDate(competition.start_date)} – ${fmtDate(competition.end_date)}` : ''}
            {competition.location ? ` · ${competition.location}` : ''}
          </div>
          {(leagueProgress || tournamentProgress) && (
            <div style={{ color: C.accent, fontSize: 12, fontWeight: 700, marginTop: 4 }}>
              {leagueProgress || tournamentProgress}
            </div>
          )}
        </div>
        {matches.length > 0 && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ background: 'rgba(16,185,129,0.15)', color: '#10b981', borderRadius: 8, padding: '4px 9px', fontSize: 12, fontWeight: 700 }}>{w}W</span>
            {d > 0 && <span style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b', borderRadius: 8, padding: '4px 9px', fontSize: 12, fontWeight: 700 }}>{d}D</span>}
            <span style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', borderRadius: 8, padding: '4px 9px', fontSize: 12, fontWeight: 700 }}>{l}L</span>
            {hasGames && (
              <span style={{ color: C.muted, fontSize: 11.5, marginLeft: 4 }}>{games.me}–{games.opp} games</span>
            )}
          </div>
        )}
      </div>

      {ratingTiles.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(ratingTiles.length, 3)}, 1fr)`, gap: 1, background: C.border, borderRadius: 10, overflow: 'hidden', margin: '10px 0' }}>
          {ratingTiles.map(t => (
            <div key={t.label} style={{ background: 'rgba(15,23,42,0.9)', padding: '10px 8px', textAlign: 'center' }}>
              <div style={{ color: C.accent, fontSize: 15, fontWeight: 800 }}>{t.value}★</div>
              <div style={{ color: C.muted, fontSize: 10, marginTop: 3 }}>{t.label}</div>
            </div>
          ))}
        </div>
      )}

      {competition.goal && (
        <div style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid rgba(56,189,248,0.35)`, borderRadius: 10, padding: '10px 14px', margin: '10px 0', color: C.text, fontSize: 13, fontStyle: 'italic' }}>
          🎯 {competition.goal}
        </div>
      )}

      {competition.format_meta?.strategy && (
        <div style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid rgba(56,189,248,0.35)`, borderRadius: 10, padding: '10px 14px', margin: '10px 0' }}>
          <div style={{ color: C.muted, fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
            Competition game plan
          </div>
          <div style={{ color: C.text, fontSize: 13, lineHeight: 1.5 }}>{competition.format_meta.strategy}</div>
        </div>
      )}

      {competition.format_meta?.review && Object.keys(competition.format_meta.review).length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(Object.keys(competition.format_meta.review).length, 4)}, 1fr)`, gap: 1, background: C.border, borderRadius: 10, overflow: 'hidden', margin: '10px 0' }}>
          {[
            { key: 'performance',        label: 'Performance' },
            { key: 'plan_outcome',       label: 'Plan outcome' },
            { key: 'tactical_execution', label: 'Tactical execution' },
            { key: 'mental_focus',       label: 'Mental focus' },
          ].filter(t => competition.format_meta.review[t.key] > 0).map(t => (
            <div key={t.key} style={{ background: 'rgba(15,23,42,0.9)', padding: '10px 8px', textAlign: 'center' }}>
              <div style={{ color: C.accent, fontSize: 15, fontWeight: 800 }}>{competition.format_meta.review[t.key]}★</div>
              <div style={{ color: C.muted, fontSize: 10, marginTop: 3 }}>{t.label}</div>
            </div>
          ))}
        </div>
      )}

      {competition.format_meta?.reflection && (
        <div style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid rgba(56,189,248,0.35)`, borderRadius: 10, padding: '10px 14px', margin: '10px 0' }}>
          <div style={{ color: C.muted, fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
            How it went
          </div>
          <div style={{ color: C.text, fontSize: 13, lineHeight: 1.5 }}>{competition.format_meta.reflection}</div>
        </div>
      )}

      {isUpcoming && daysLeft != null && daysLeft >= 0 && (
        <div style={{ color: C.accent, fontSize: 12.5, fontWeight: 700, marginBottom: 12 }}>
          {daysLeft === 0 ? "Today's the day" : `${daysLeft} day${daysLeft === 1 ? '' : 's'} to go`}
        </div>
      )}

      {linked.length === 0 ? (
        <div style={{ color: C.dim, fontSize: 13, fontStyle: 'italic', padding: '20px 0' }}>
          No Game Plans linked to this competition yet.
        </div>
      ) : (
        Object.entries(groups).map(([label, items]) => (
          <div key={label} style={{ marginBottom: 16 }}>
            <div style={{ color: C.muted, fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
              {label}
            </div>
            {items.map(p => (
              <div key={p.id}
                onClick={onOpenDetail ? () => onOpenDetail(p) : undefined}
                style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                background: 'rgba(255,255,255,0.04)', border: `1px solid rgba(56,189,248,0.30)`, borderRadius: 10,
                padding: '9px 12px', marginBottom: 6,
                cursor: onOpenDetail ? 'pointer' : 'default',
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: onOpenDetail ? C.accent : C.text, fontSize: 13, fontWeight: 600, textDecoration: onOpenDetail ? 'underline' : 'none', textDecorationColor: 'rgba(52,211,153,0.35)' }}>
                    {fmtDate(p.scheduled_date)} · {p.opponent_name ? opponentLabel(p) : (p.goal || p.type)}
                  </div>
                  {p.post_notes && (
                    <div style={{ color: C.muted, fontSize: 11.5, marginTop: 2, fontStyle: 'italic' }}>{p.post_notes.slice(0, 90)}</div>
                  )}
                </div>
                {p.result && (
                  <span style={{
                    flexShrink: 0, fontSize: 11, fontWeight: 800, borderRadius: 6, padding: '3px 8px',
                    color: p.result === 'win' ? '#10b981' : p.result === 'loss' ? '#ef4444' : '#f59e0b',
                    background: p.result === 'win' ? 'rgba(16,185,129,0.14)' : p.result === 'loss' ? 'rgba(239,68,68,0.14)' : 'rgba(245,158,11,0.14)',
                  }}>{p.result.toUpperCase()}</span>
                )}
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
}

function CompetitionsView({ plans, userId, demoMode, activeSport, onOpenDetail }) {
  const [competitions, setCompetitions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const isMobile = useIsMobile();
  const [reviewPicked, setReviewPicked] = useState([]);
  const togglePick = setter => key => setter(cur => cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key]);

  useEffect(() => {
    if (demoMode) {
      fetch(`${API}/game-plans/demo/competitions`)
        .then(r => r.json()).then(d => setCompetitions(d.competitions || [])).catch(e => console.error('[CompetitionsView] demo competitions load failed:', e));
      return;
    }
    if (!userId) return;
    let q = supabase.from('competitions').select('*').eq('user_id', userId);
    if (activeSport) q = q.eq('sport', activeSport);
    q.order('start_date', { ascending: false }).then(({ data }) => setCompetitions(data || []));
  }, [userId, demoMode, activeSport]);

  if (selected) {
    return <CompetitionDetail competition={selected} plans={plans} onBack={() => setSelected(null)} onOpenDetail={onOpenDetail} />;
  }

  const active = competitions.filter(c => c.status !== 'archived');
  const archived = competitions.filter(c => c.status === 'archived');

  const matchesFor = (c) => plans.filter(p => p.competition_id === c.id && ['match', 'competition'].includes(p.type) && p.result);
  const recordFor = (c) => {
    const matches = matchesFor(c);
    const w = matches.filter(p => p.result === 'win').length;
    const d = matches.filter(p => p.result === 'draw').length;
    const l = matches.filter(p => p.result === 'loss').length;
    return matches.length ? (d > 0 ? `${w}W-${d}D-${l}L` : `${w}W-${l}L`) : null;
  };
  const avgReview = (c) => {
    const vals = Object.values(c.format_meta?.review || {}).filter(v => v > 0);
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  };

  const Card = ({ c }) => {
    const avg = avgReview(c);
    return (
      <div onClick={() => setSelected(c)} role="button" tabIndex={0} onKeyDown={onEnterOrSpace(() => setSelected(c))} style={{
        cursor: 'pointer', background: 'rgba(255,255,255,0.04)', border: `1.5px solid ${C.moduleBorder}`,
        borderRadius: 12, padding: '13px 16px', marginBottom: 8,
        opacity: c.status === 'archived' ? 0.55 : 1,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ color: C.logoBlue, fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{competitionIconGP(c.type)} {c.name}</span>
            {c.goal_status === 'achieved' && <span style={{ flexShrink: 0, color: '#22c55e', fontSize: 13 }} title="Goal achieved">✓</span>}
            {c.goal_status === 'not_achieved' && <span style={{ flexShrink: 0, color: '#ef4444', fontSize: 13 }} title="Goal not achieved">✗</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {avg != null && <span style={{ color: C.accent, fontSize: 11.5, fontWeight: 700 }}>{avg.toFixed(1)}★</span>}
            {recordFor(c) && <span style={{ color: C.muted, fontSize: 12, fontWeight: 700 }}>{recordFor(c)}</span>}
          </div>
        </div>
        <div style={{ color: C.muted, fontSize: 11.5, marginTop: 3 }}>
          {c.type}{c.start_date ? ` · ${fmtDate(c.start_date)} – ${fmtDate(c.end_date)}` : ''}
        </div>
      </div>
    );
  };

  // "Closing the loop" — once a competition is archived, how did it get
  // rated? Only ones that actually went through Result review (closing
  // without ever reviewing it doesn't belong on this trend), oldest first
  // so the line reads left-to-right like the other Analytics charts.
  const closedWithReview = archived
    .filter(c => c.format_meta?.review && Object.values(c.format_meta.review).some(v => v > 0))
    .sort((a, b) => (a.end_date || a.start_date || '').localeCompare(b.end_date || b.start_date || ''));
  const reviewChartData = closedWithReview.map(c => ({
    name: c.name.length > 16 ? `${c.name.slice(0, 15)}…` : c.name,
    fullName: c.name,
    perf: c.format_meta.review.performance || null,
    plan: c.format_meta.review.plan_outcome || null,
    tactical: c.format_meta.review.tactical_execution || null,
    mental: c.format_meta.review.mental_focus || null,
  }));

  const achievedCount = competitions.filter(c => c.goal_status === 'achieved').length;
  const notAchievedCount = competitions.filter(c => c.goal_status === 'not_achieved').length;

  const cardStyle = {
    background: 'rgba(15,23,42,0.82)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
    border: '1.5px solid rgba(56,189,248,0.65)', borderRadius: 14,
    padding: '20px 20px 16px', marginBottom: 20,
  };
  const chartH = isMobile ? 220 : 280;
  const sectionTitle = (title, sub) => (
    <div style={{ marginBottom: 12 }}>
      <div style={{ color: C.text, fontWeight: 800, fontSize: 14 }}>{title}</div>
      {sub && <div style={{ color: C.muted, fontSize: 11, marginTop: 2 }}>{sub}</div>}
    </div>
  );
  const CompTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    return (
      <div style={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, padding: '10px 14px', fontSize: 12, minWidth: 160 }}>
        <div style={{ color: '#94a3b8', marginBottom: 6, fontWeight: 700 }}>{d?.fullName}</div>
        {payload.map((p, i) => (
          <div key={i} style={{ color: p.color, marginBottom: 2 }}>{p.name}: <strong>{p.value}★</strong></div>
        ))}
      </div>
    );
  };

  if (competitions.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0', color: C.muted }}>
        <div style={{ fontSize: 32, marginBottom: 10 }}>🏆</div>
        <div style={{ fontSize: 14 }}>No competitions yet.</div>
        <div style={{ fontSize: 12.5, marginTop: 4 }}>Add a league or tournament from the Game Plan tab.</div>
      </div>
    );
  }

  return (
    <div>
      {/* Overview strip — the question this tab should answer at a glance:
          what's live right now, and has the goal-setting actually paid off
          across everything closed so far. */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${achievedCount + notAchievedCount > 0 ? 3 : 2}, 1fr)`, gap: 1, background: C.border, borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ background: 'rgba(15,23,42,0.9)', padding: '10px 8px', textAlign: 'center' }}>
          <div style={{ color: C.accent, fontSize: 17, fontWeight: 800 }}>{active.length}</div>
          <div style={{ color: C.muted, fontSize: 10, marginTop: 2 }}>Active</div>
        </div>
        <div style={{ background: 'rgba(15,23,42,0.9)', padding: '10px 8px', textAlign: 'center' }}>
          <div style={{ color: C.text, fontSize: 17, fontWeight: 800 }}>{archived.length}</div>
          <div style={{ color: C.muted, fontSize: 10, marginTop: 2 }}>Closed</div>
        </div>
        {achievedCount + notAchievedCount > 0 && (
          <div style={{ background: 'rgba(15,23,42,0.9)', padding: '10px 8px', textAlign: 'center' }}>
            <div style={{ fontSize: 17, fontWeight: 800 }}>
              <span style={{ color: '#22c55e' }}>{achievedCount}✓</span>{' '}
              <span style={{ color: '#ef4444' }}>{notAchievedCount}✗</span>
            </div>
            <div style={{ color: C.muted, fontSize: 10, marginTop: 2 }}>Goals</div>
          </div>
        )}
      </div>

      {active.map(c => <Card key={c.id} c={c} />)}

      {/* Closing the loop — how closed competitions were actually rated,
          so the goal/review data collected on each one adds up to
          something instead of living only on its own detail page. */}
      {closedWithReview.length >= 2 && (
        <div style={cardStyle}>
          {/* Grouped bars, not a line — these are separate, unrelated
              competitions laid out along a timeline (oldest to newest), not
              a continuous trend, so a line between them implied a
              connection that wasn't real. Bars side by side per
              competition make them directly comparable instead. Feedback
              2026-11-01. */}
          {sectionTitle('📊 How closed competitions were rated', '★ rating recorded when each one closed — oldest to newest')}
          <ResponsiveContainer width="100%" height={chartH}>
            <BarChart data={reviewChartData} margin={{ top: 4, right: 12, left: 0, bottom: 4 }} barCategoryGap="22%" barGap={3}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="name" tick={{ fill: C.muted, fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis domain={[0, 5]} ticks={[1, 2, 3, 4, 5]} tickFormatter={v => `${v}★`} tick={{ fill: C.muted, fontSize: 10 }} tickLine={false} axisLine={false} width={32} />
              {!isMobile && <Tooltip content={<CompTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />}
              <Legend content={<ClickableLegend selected={reviewPicked} onToggle={togglePick(setReviewPicked)} onShowAll={() => setReviewPicked([])} />} />
              <Bar dataKey="perf" name="Performance ★" fill="#f59e0b" radius={[3, 3, 0, 0]} hide={reviewPicked.length > 0 && !reviewPicked.includes('perf')} />
              <Bar dataKey="plan" name="Plan outcome ★" fill={C.accent} radius={[3, 3, 0, 0]} hide={reviewPicked.length > 0 && !reviewPicked.includes('plan')} />
              <Bar dataKey="tactical" name="Tactical execution ★" fill="#a78bfa" radius={[3, 3, 0, 0]} hide={reviewPicked.length > 0 && !reviewPicked.includes('tactical')} />
              <Bar dataKey="mental" name="Mental focus ★" fill="#f472b6" radius={[3, 3, 0, 0]} hide={reviewPicked.length > 0 && !reviewPicked.includes('mental')} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {archived.length > 0 && (
        <>
          <button onClick={() => setShowArchived(s => !s)} style={{
            background: 'none', border: 'none', color: C.muted, fontSize: 11.5,
            cursor: 'pointer', fontFamily: C.font, padding: '6px 0', textDecoration: 'underline',
          }}>{showArchived ? 'Hide' : 'Show'} archived ({archived.length})</button>
          {showArchived && archived.map(c => <Card key={c.id} c={c} />)}
        </>
      )}
    </div>
  );
}

// ── Heads Up Analytics ───────────────────────────────────────────────────────

function HeadsUpView({ plans, userId, demoMode, activeSport, onOpenDetail }) {
  const isMobile = useIsMobile();
  const [opponents, setOpponents] = useState([]);
  const [expanded,  setExpanded]  = useState(null); // opponent id

  useEffect(() => {
    if (demoMode) {
      fetch(`${API}/game-plans/demo/opponents`)
        .then(r => r.json())
        .then(d => setOpponents(d.opponents || []))
        .catch(e => console.error('[HeadsUpView] demo opponents load failed:', e));
      return;
    }
    if (!userId) return;
    let q = supabase.from('opponents').select('*').eq('user_id', userId);
    if (activeSport) q = q.eq('sport', activeSport);
    q.order('name')
      .then(({ data }) => setOpponents(data || []));
  }, [userId, demoMode, activeSport]);

  // Group plans by opponent_id — a doubles match is added to BOTH opponents'
  // buckets (opponent_id and opponent2_id, if set), so an athlete's
  // head-to-head record against a given person includes every match played
  // against them, singles or doubles. See docs_zenanalytics/06-DOUBLES-SUPPORT-SPEC-2026-09-25.md §4.
  const byOpponent = useMemo(() => {
    const matchPlans = plans.filter(p =>
      ['match','competition'].includes(p.type) && p.opponent_id && p.scheduled_date <= today()
    );
    const map = {};
    matchPlans.forEach(p => {
      if (!map[p.opponent_id]) map[p.opponent_id] = [];
      map[p.opponent_id].push(p);
      if (p.opponent2_id && p.opponent2_id !== p.opponent_id) {
        if (!map[p.opponent2_id]) map[p.opponent2_id] = [];
        map[p.opponent2_id].push(p);
      }
    });
    return map;
  }, [plans]);

  const rows = useMemo(() => {
    return opponents
      .filter(o => byOpponent[o.id]?.length > 0)
      .map(o => {
        const ps = byOpponent[o.id] || [];
        const w  = ps.filter(p => p.result === 'win').length;
        const d  = ps.filter(p => p.result === 'draw').length;
        const l  = ps.filter(p => p.result === 'loss').length;
        const perfScores = ps.filter(p => p.performance_score).map(p => p.performance_score);
        const planScores = ps.filter(p => p.game_plan_adherence).map(p => p.game_plan_adherence);
        const avgPerf = perfScores.length ? (perfScores.reduce((a,b)=>a+b,0)/perfScores.length).toFixed(1) : null;
        const avgPlan = planScores.length ? (planScores.reduce((a,b)=>a+b,0)/planScores.length).toFixed(1) : null;
        return { opponent: o, sessions: ps.sort((a,b)=>b.scheduled_date.localeCompare(a.scheduled_date)), w, d, l, avgPerf, avgPlan };
      })
      .sort((a,b) => b.sessions.length - a.sessions.length);
  }, [opponents, byOpponent]);

  const resultColor = r => r === 'win' ? '#10b981' : r === 'draw' ? '#f59e0b' : r === 'loss' ? '#ef4444' : C.muted;
  const resultLabel = r => r === 'win' ? 'W' : r === 'draw' ? 'D' : r === 'loss' ? 'L' : '—';
  const stars = n => n ? '★'.repeat(Math.round(n)) + '☆'.repeat(5 - Math.round(n)) : '—';

  if (rows.length === 0) return (
    <div style={{
      background: 'rgba(255,255,255,0.04)', border: '1px dashed rgba(255,255,255,0.15)',
      borderRadius: 12, padding: '40px 24px', textAlign: 'center',
    }}>
      <div style={{ fontSize: 36, marginBottom: 10 }}>🥊</div>
      <div style={{ color: C.text, fontWeight: 700, fontSize: 15, marginBottom: 6 }}>No head-to-head records yet</div>
      <div style={{ color: C.muted, fontSize: 13, lineHeight: 1.6 }}>
        Log matches or competitions with an opponent name to start building your records.
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ color: C.muted, fontSize: 12, marginBottom: 16 }}>
        {rows.length} opponent{rows.length !== 1 ? 's' : ''} · click a row to see match history
      </div>
      {rows.map(({ opponent, sessions, w, d, l, avgPerf, avgPlan }) => {
        const isOpen = expanded === opponent.id;
        const typeIcon = opponent.type === 'team' ? '🏟️' : opponent.type === 'event' ? '🏆' : '👤';
        return (
          <div key={opponent.id} style={{ marginBottom: 8, borderRadius: 10, overflow: 'hidden',
            border: `1.5px solid ${isOpen ? 'rgba(61,191,176,0.35)' : C.moduleBorder}`,
          }}>
            {/* Summary row */}
            <div
              onClick={() => setExpanded(isOpen ? null : opponent.id)}
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr 36px 36px 36px 28px' : '1fr 60px 60px 60px 90px 90px 28px',
                alignItems: 'center', gap: 4,
                padding: '12px 14px',
                background: isOpen ? 'rgba(61,191,176,0.08)' : 'rgba(255,255,255,0.03)',
                cursor: 'pointer',
              }}
            >
              <div>
                <span style={{ fontSize: 14 }}>{typeIcon}</span>
                <span style={{ color: C.logoBlue, fontWeight: 600, fontSize: 13, marginLeft: 6 }}>{opponent.name}</span>
                <span style={{ color: C.muted, fontSize: 11, marginLeft: 6 }}>
                  {sessions.length}g
                </span>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#10b981', fontWeight: 800, fontSize: isMobile ? 14 : 16 }}>{w}</div>
                <div style={{ color: C.muted, fontSize: 9 }}>W</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#f59e0b', fontWeight: 800, fontSize: isMobile ? 14 : 16 }}>{d}</div>
                <div style={{ color: C.muted, fontSize: 9 }}>D</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#ef4444', fontWeight: 800, fontSize: isMobile ? 14 : 16 }}>{l}</div>
                <div style={{ color: C.muted, fontSize: 9 }}>L</div>
              </div>
              {!isMobile && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: C.accent, fontSize: 12 }}>{avgPerf ? `${avgPerf}★` : '—'}</div>
                  <div style={{ color: C.muted, fontSize: 10 }}>Avg perf</div>
                </div>
              )}
              {!isMobile && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: C.purple, fontSize: 12 }}>{avgPlan ? `${avgPlan}★` : '—'}</div>
                  <div style={{ color: C.muted, fontSize: 10 }}>Avg plan</div>
                </div>
              )}
              <div style={{ color: C.muted, fontSize: 14, textAlign: 'center' }}>
                {isOpen ? '▲' : '▼'}
              </div>
            </div>

            {/* Match history */}
            {isOpen && (
              <div style={{ background: 'rgba(0,0,0,0.25)', padding: '8px 14px 12px' }}>
                {sessions.map(s => (
                  <div key={s.id}
                    onClick={onOpenDetail ? () => onOpenDetail(s) : undefined}
                    style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '60px 1fr 32px 60px' : '80px 1fr 40px 80px 80px',
                    alignItems: 'center', gap: 8,
                    padding: '8px 0',
                    borderBottom: `1px solid ${C.border}`,
                    fontSize: 12,
                    cursor: onOpenDetail ? 'pointer' : 'default',
                  }}>
                    <div style={{ color: C.muted, fontSize: isMobile ? 11 : 12 }}>{fmtDate(s.scheduled_date?.slice(0,10))}</div>
                    <div style={{ color: onOpenDetail ? C.accent : C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: onOpenDetail ? 'underline' : 'none', textDecorationColor: 'rgba(52,211,153,0.35)' }}>
                      {formatBadge(s) && <span style={{ color: C.muted, fontWeight: 700, marginRight: 4 }}>{formatBadge(s)}</span>}
                      {s.goal || s.type}
                    </div>
                    <div style={{
                      fontWeight: 800, fontSize: 13,
                      color: resultColor(s.result),
                      textAlign: 'center',
                    }}>{resultLabel(s.result)}</div>
                    <div style={{ color: C.accent, textAlign: 'center', fontSize: 11 }}>
                      {s.performance_score ? `${s.performance_score}★` : '—'}
                    </div>
                    {!isMobile && (
                      <div style={{ color: C.muted, textAlign: 'center', fontSize: 11 }}>
                        {s.game_plan_adherence ? `${s.game_plan_adherence}★ plan` : '—'}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function GamePlanView({ session, initialTab, onInitialTabConsumed, readOnly = false, demoMode = false, activeSport, onOpenPlan }) {
  const userId = session?.user?.id;

  const [plans,      setPlans]      = useState([]);
  // Full isolation between sports (2026-10-18, PRODUCT-GUIDELINES.md §5).
  const visiblePlans = useSportFiltered(plans, activeSport);
  const [loading,    setLoading]    = useState(true);
  const [detail,     setDetail]     = useState(null); // session being viewed — fallback only, see openPlanDirect
  const [activeTab,  setActiveTab]  = useState(initialTab || 'dashboard');

  // Season filter (2026-10-30, docs_zenanalytics/26-SEASON-FILTER-GAME-
  // ANALYTICS-2026-10-30.md, revised same day per user feedback) — reuses
  // the athlete's own season_goals entities rather than inventing a new
  // concept; granularity (quarter, half, full year) is entirely up to how
  // the athlete names/dates their own seasons. Presented as exactly three
  // buttons rather than a dropdown listing every season by name — "Ongoing
  // season" / "Previous season" / "All seasons" — so the athlete always
  // knows at a glance which of three simple buckets they're looking at,
  // instead of having to recognize a specific season's name. Defaults to
  // "Ongoing season" (not "All seasons"): the question Dashboard should
  // answer by default is "how am I doing right now", not the lifetime
  // total. Fetches ALL statuses, not just 'active' — a completed season
  // must stay reachable as "Previous". Demo mode has no multi-season
  // endpoint yet, so the buttons simply don't appear there — demo is
  // read-only/synthetic, not a real gap.
  const [seasons, setSeasons] = useState([]);
  // Heads Up defaults to "All seasons" -- it's a head-to-head record, and
  // a record scoped to just the current season reads as "wrong" next to
  // Home's all-time Top-5 Heads-Up card for the same opponent. Every other
  // tab still opens on "Ongoing season". See switchTab below for the
  // same default when navigating there mid-session.
  const [seasonMode, setSeasonMode] = useState((initialTab || 'dashboard') === 'headsup' ? 'all' : 'ongoing'); // 'ongoing' | 'previous' | 'all'

  useEffect(() => {
    if (demoMode || !userId) return;
    let cancelled = false;
    let q = supabase.from('season_goals').select('id,name,title,start_date,end_date,status').eq('user_id', userId);
    if (activeSport) q = q.eq('sport', activeSport);
    q.order('start_date', { ascending: false }).then(({ data }) => {
      if (!cancelled) setSeasons(data || []);
    });
    return () => { cancelled = true; };
  }, [userId, demoMode, activeSport]);

  // A season with no explicit start_date can't anchor a range at all —
  // excluded from both buckets below rather than silently matching
  // everything.
  const pickableSeasons = useMemo(() => seasons.filter(s => s.start_date), [seasons]);

  // "Ongoing" — the season whose range actually contains today. If more
  // than one somehow overlaps today, the one that started most recently
  // wins (pickableSeasons is already sorted start_date desc).
  const ongoingSeason = useMemo(() => {
    const today = todayLocal();
    return pickableSeasons.find(s => {
      const start = s.start_date.slice(0, 10);
      const end = s.end_date ? s.end_date.slice(0, 10) : null;
      return start <= today && (!end || end >= today);
    }) || null;
  }, [pickableSeasons]);

  // "Previous" — the most recent season that isn't the ongoing one and
  // has already ended (or, lacking an end_date, started before the
  // ongoing one did). The single season immediately before "now", not
  // just "some other season".
  const previousSeason = useMemo(() => {
    const today = todayLocal();
    const others = pickableSeasons.filter(s => s.id !== ongoingSeason?.id);
    const ended = others.filter(s => {
      const end = s.end_date ? s.end_date.slice(0, 10) : null;
      return end ? end < today : (ongoingSeason ? s.start_date.slice(0, 10) < ongoingSeason.start_date.slice(0, 10) : false);
    });
    // Already sorted start_date desc from the query, so the first ended
    // season in that order is the most recent one.
    return ended[0] || null;
  }, [pickableSeasons, ongoingSeason]);

  const activeSeason = seasonMode === 'ongoing' ? ongoingSeason : seasonMode === 'previous' ? previousSeason : null;

  // Pure date-range membership against scheduled_date — deliberately NOT
  // via competition linkage, so a standalone training or an unlinked match
  // still counts as part of the season it was actually played in.
  const seasonFilteredPlans = useMemo(() => {
    if (!activeSeason) return visiblePlans;
    const from = activeSeason.start_date.slice(0, 10);
    const to = activeSeason.end_date ? activeSeason.end_date.slice(0, 10) : todayLocal();
    return visiblePlans.filter(p => {
      const d = p.scheduled_date?.slice(0, 10);
      return d && d >= from && d <= to;
    });
  }, [visiblePlans, activeSeason]);

  // Reset to "Ongoing season" whenever the sport switches — a season
  // picked for tennis has no meaning once the SportSwitcher moves to padel.
  useEffect(() => { setSeasonMode('ongoing'); }, [activeSport]);

  // Drilling into a session from any Game Stats list used to open a
  // read-only summary popup first, requiring a second click ("Open in Game
  // Plan") to actually get to the plan/result. Per user feedback
  // 2026-10-02, this now jumps straight to the real Game Plan editor —
  // SessionModal already lands on its Result tab when the plan has a
  // result/performance_score and its Plan tab otherwise, so no explicit
  // tab override is even needed here. Falls back to the old summary popup
  // only if no onOpenPlan is wired at all.
  const openPlanDirect = (plan) => (onOpenPlan ? onOpenPlan(plan) : setDetail(plan));

  // Consume initialTab once rendered
  useEffect(() => {
    if (initialTab && onInitialTabConsumed) onInitialTabConsumed();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load game plans from Supabase directly (demo mode: public demo endpoint, no auth)
  const loadPlans = useCallback(async () => {
    if (demoMode) {
      try {
        const res = await fetch(`${API}/game-plans/demo`);
        const data = res.ok ? await res.json() : { plans: [] };
        setPlans(data.plans || []);
      } catch (e) {
        console.error('loadPlans demo exception:', e);
      } finally {
        setLoading(false);
      }
      return;
    }
    if (!userId) { setLoading(false); return; }
    const fallback = setTimeout(() => setLoading(false), 8000);
    try {
      const { data, error } = await fetchAllPlans(userId);
      if (error) console.error('loadPlans error:', error);
      setPlans(data || []);
    } catch (e) {
      console.error('loadPlans exception:', e);
    } finally {
      clearTimeout(fallback);
      setLoading(false);
    }
  }, [userId, demoMode]);

  useEffect(() => { loadPlans(); }, [loadPlans]);


  if (loading) return (
    <div style={{ minHeight: '100vh', position: 'relative', paddingBottom: 80, background: C.bg }}>
      <div style={{
        margin: '16px', padding: 40, textAlign: 'center', color: C.muted,
        background: 'rgba(15,23,42,0.82)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
        borderRadius: 14, border: '1px solid rgba(255,255,255,0.10)', position: 'relative', zIndex: 1,
      }}>Loading game stats…</div>
    </div>
  );

  const TABS = [
    { id: 'dashboard',    label: '📊 Dashboard' },
    { id: 'analytics',    label: '📈 Analytics' },
    { id: 'headsup',      label: '🆚 Heads Up' },
    { id: 'competitions', label: '🏆 Competitions' },
    { id: 'equipment',    label: '🎾 Stats by Equipment' },
    { id: 'library',      label: '📚 Notes Library' },
  ];

  const isMobile = window.innerWidth < 640;

  const switchTab = (id) => {
    setActiveTab(id);
    if (id === 'headsup') setSeasonMode('all');
  };

  return (
    <div style={{ minHeight: '100vh', position: 'relative', paddingBottom: 80, background: C.bg }}>
    <div style={{
      fontFamily: C.font, margin: isMobile ? '8px' : '16px',
      background: 'rgba(15,23,42,0.82)',
      backdropFilter: 'blur(14px)',
      WebkitBackdropFilter: 'blur(14px)',
      borderRadius: 14,
      border: '1px solid rgba(255,255,255,0.10)',
      padding: isMobile ? '14px 12px 24px' : '20px 24px 32px',
      color: C.text,
      position: 'relative',
      zIndex: 1,
    }}>
      {/* Header */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ color: C.text, fontSize: 15, fontWeight: 700, marginBottom: 4 }}>
          📊 Game analytics
        </div>
        {!isMobile && (
          <div style={{ color: C.muted, fontSize: 12 }}>
            Your training history · Performance trends · Head-to-head records
          </div>
        )}
      </div>

      {/* Tab switcher */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, borderBottom: `1px solid ${C.border}`, paddingBottom: 10, overflowX: 'auto' }}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => switchTab(tab.id)}
            style={{
              padding: isMobile ? '6px 12px' : '7px 16px', borderRadius: 8, fontWeight: 600,
              fontSize: isMobile ? 12 : 13, whiteSpace: 'nowrap',
              fontFamily: C.font, cursor: 'pointer',
              background: activeTab === tab.id ? 'rgba(61,191,176,0.15)' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${activeTab === tab.id ? 'rgba(61,191,176,0.50)' : C.border}`,
              color: activeTab === tab.id ? C.accent : C.muted,
              transition: 'all 0.13s', position: 'relative',
            }}>
            {tab.label}
            {tab.badge != null && (
              <span style={{
                marginLeft: 6,
                background: '#ef4444', color: '#fff',
                borderRadius: 20, padding: '1px 6px',
                fontSize: 10, fontWeight: 800,
              }}>{tab.badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* Season filter — scopes whichever tab is active below, EXCEPT
          Stats by Equipment. Sits right above the tab's own content (same
          placement pattern Analytics' own Time/Type filter bar already
          uses), not up in the page header, per user feedback 2026-10-30.
          Excluded from Equipment (user feedback, same day, confirmed by
          testing): a stringing's own usage stats are inherently
          season-independent — "since 2026-04-04 · 125 days" next to a
          match count that's silently been cut to only the ongoing season
          is actively misleading, not just unhelpful. Equipment tracks wear
          and usage across the gear's whole life, not per season, so the
          filter is hidden here rather than shown but ignored. */}
      {pickableSeasons.length > 0 && activeTab !== 'equipment' && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {[
            { id: 'ongoing',  label: 'Ongoing season',  season: ongoingSeason,  disabled: !ongoingSeason },
            { id: 'previous', label: 'Previous season', season: previousSeason, disabled: !previousSeason },
            { id: 'all',      label: 'All seasons',     season: null,           disabled: false },
          ].map(opt => {
            const active = seasonMode === opt.id;
            const name = opt.season ? (opt.season.name || opt.season.title) : null;
            return (
              <button
                key={opt.id}
                onClick={() => !opt.disabled && setSeasonMode(opt.id)}
                disabled={opt.disabled}
                title={name ? `${opt.label}: ${name}` : (opt.disabled ? `No ${opt.label.toLowerCase()} found` : opt.label)}
                style={{
                  background: active ? 'rgba(61,191,176,0.18)' : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${active ? C.accent : C.border}`,
                  borderRadius: 20, padding: '6px 13px', fontSize: 12.5, fontWeight: 700,
                  color: active ? C.accent : C.muted,
                  cursor: opt.disabled ? 'default' : 'pointer', fontFamily: C.font,
                  opacity: opt.disabled ? 0.5 : 1, whiteSpace: 'nowrap',
                }}
              >{opt.label}</button>
            );
          })}
        </div>
      )}

      {activeTab === 'dashboard' ? (
        <SessionsDashboard plans={seasonFilteredPlans} userId={userId} activeSport={activeSport} onOpenDetail={openPlanDirect} demoMode={demoMode} />
      ) : activeTab === 'analytics' ? (
        <AnalyticsView plans={seasonFilteredPlans} onOpenDetail={openPlanDirect} />
      ) : activeTab === 'headsup' ? (
        <HeadsUpView plans={seasonFilteredPlans} userId={userId} demoMode={demoMode} activeSport={activeSport} onOpenDetail={openPlanDirect} />
      ) : activeTab === 'competitions' ? (
        <CompetitionsView plans={seasonFilteredPlans} userId={userId} demoMode={demoMode} activeSport={activeSport} onOpenDetail={openPlanDirect} />
      ) : activeTab === 'equipment' ? (
        <EquipmentStatsView plans={visiblePlans} userId={userId} activeSport={activeSport} demoMode={demoMode} />
      ) : (
        <NotesLibraryView plans={seasonFilteredPlans} />
      )}

      {/* Session detail modal */}
      {detail && (
        <SessionDetailModal
          plan={detail}
          onClose={() => setDetail(null)}
          demoMode={demoMode}
          onOpenPlan={onOpenPlan ? () => { onOpenPlan(detail); setDetail(null); } : null}
        />
      )}
    </div>
    </div>
  );
}
